import { Component, Input, OnInit, OnDestroy, SimpleChanges, OnChanges, ElementRef, ViewChild } from '@angular/core';
import { GetGroupingCodeListService } from './api-services/get-grouping-code-list.service';
import { GroupingCodeComboBoxModel } from './grouping-code-combo-box.model';
import { PlessHttp } from '../../../../shared/http/pless-http.service';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { PlessDialogServiceSub } from '../../../../commons/kymw00/pless-dialog-service-sub';

/**
 * 共通部品 ECグルーピングコンボボックスを制御するクラス
 *
 * @export GroupingCodeComboBoxComponent
 * @class GroupingCodeComboBoxComponent
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnChanges}
 */
@Component({
  selector: 'app-grouping-code-combo-box',
  templateUrl: 'grouping-code-combo-box.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: GroupingCodeComboBoxComponent,
      multi: true
    },
    {
      provide: GetGroupingCodeListService,
      useFactory: (plessHttp: PlessHttp, dialogService: PlessDialogServiceSub) =>
          new GetGroupingCodeListService(plessHttp, dialogService),
          deps: [PlessHttp, PlessDialogServiceSub]
        }
  ],
})

export class GroupingCodeComboBoxComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {

  public selectedGroupingCode: string = <string>'';
  public selectedGroupingName: string = <string>'';
  public selectedIndex: number = <number>-1;                                    // 選択した番号

  public model: GroupingCodeComboBoxModel = new GroupingCodeComboBoxModel();

  // グルーピング対象区分
  private _groupingDiv142: string = <string>'0';
  private _groupingDiv163: string = <string>'0';
  private _groupingDiv185: string = <string>'0';
  private _groupingDiv240: string = <string>'0';
  private _groupingDiv210: string = <string>'0';
  private _groupingDiv302: string = <string>'0';
  private _groupingDiv515: string = <string>'0';
  private _groupingDiv451: string = <string>'0';
  private _groupingDiv454: string = <string>'0';

  @ViewChild('comboBox', { static: true }) public comboBox: ElementRef;

  @Input() public subid: string = null;
  @Input() blank: boolean = <boolean>true;                                      // 空白フラグ
  @Input() initAutoLoad: boolean = <boolean>true;
  @Input() public tabindex: number = null;
  @Input() forcedEnabledMode: string = null;

  // レイアウト系の変数
  @Input() left: number = <number>0;
  @Input() top: number = <number>0;
  @Input() width: number | string;
  @Input() height: number = <number>20;
  @Input() disabled: boolean = <boolean>false;
  @Input() required: boolean = <boolean>false;
  @Input() initFocusItem: any = null;                                           // ロード完了後フォーカスを移動させるid
  @Input() afterFocus: any = null;                                              // ロード完了後フォーカスを移動させるid
  @Input() visible: boolean = <boolean>true;                                    // 表示・非表示フラグ


  constructor(
    public elementRef: ElementRef,
    public getGroupingCodeListService: GetGroupingCodeListService
  ) { }

  // ValueAccessor
  public onChange(_: any) { }
  public onTouched() { }
  public writeValue(text: string): void {
    if (text !== this.selectedGroupingCode) {
      this.selectedGroupingCode = text;
      this.setGroupingCode(text);
    }
  }
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  /**
   * 初期処理
   *
   * @memberof GroupingCodeComboBoxComponent
   */
  ngOnInit(): void {
    this.model.component = this;
    this.comboBox.nativeElement.setAttribute('id', this.subid + '_sub');
    this.comboBox.nativeElement.setAttribute('forcedEnabledMode', this.forcedEnabledMode);
  }

  /**
   * リロード
   *
   * @memberof GroupingCodeComboBoxComponent
   */
  public reload(): void {
    if (this.model.initAutoLoad) {
      // データ取得
      this.getGroupingCodeListService.execute(this.model,
        this._groupingDiv142, this._groupingDiv163, this._groupingDiv185,
        this._groupingDiv240, this._groupingDiv210, this._groupingDiv302,
        this._groupingDiv515, this._groupingDiv451, this._groupingDiv454);
    }
  }

  /**
   * コンポーネント終了時
   *
   * @memberof GroupingCodeComboBoxComponent
   */
  ngOnDestroy(): void {
    this.selectedGroupingCode = null;
    this.selectedGroupingName = null;
    this.selectedIndex = null;
    this.model = null;
  }

  /**
   * 値変更時イベント
   *
   * @param {SimpleChanges} changes 変更イベント
   * @memberof codeComboBox
   */
  ngOnChanges(changes: SimpleChanges): void {
    Object.keys(changes).forEach((propName) => {
      switch (propName) {
        case 'blank':
          // 先頭空白表示フラグ
          this.model.blank = changes.blank.currentValue;
          break;
        case 'initAutoLoad':
          // 初期データ自動取得フラグ
          this.model.initAutoLoad = changes.initAutoLoad.currentValue;
          break;
      }
    });
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @param {string} groupingCode
   * @memberof GroupingCodeComboBoxComponent
   */
  public setGroupingCode(groupingCode: string): void {
    this.model.setGroupingCode = groupingCode;

    // ロード完了の場合
    if (this.model.completeUpdateFlg) {
      this.updateSelectedGroupingCode();
    }
  }

  /**
   * コンボボックスの選択コードNameを切り替える
   *
   * @memberof GroupingCodeComboBoxComponent
   */
  public updateSelectedGroupingCode(): void {
    // 一旦クリア
    this.selectedIndex = <number>0;
    this.selectedGroupingCode = null;
    this.selectedGroupingName = null;

    const groupingList: Array<any> = this.model.groupingList;
    for (let i = Number(0); i < groupingList.length; i++) {
      if (groupingList[i].GroupingCode === this.model.setGroupingCode) {
        this.selectedIndex = i;
        this.selectedGroupingCode = groupingList[i].GroupingCode;
        this.selectedGroupingName = groupingList[i].GroupingName;
      }
    }
    if (this.initFocusItem != null) {
      if (this.initFocusItem instanceof HTMLElement) {
        // 参照先の型がElementならそのままフォーカスをあてる
        this.initFocusItem.focus();
      } else {
        // 参照先の型がElementでない(Component)ならComponent内の要素を見て最初のinput,textarea,selectElementにフォーカスをあてる
        for (const child of this.initFocusItem.elementRef.nativeElement.children) {
          if (child instanceof HTMLInputElement || child instanceof HTMLTextAreaElement || child instanceof HTMLSelectElement) {
            child.focus();
            break;
          }
        }
      }

      this.initFocusItem = null;
    }
  }

  /**
   * グルーピング対象区分
   */
  public groupingDiv142(flg: string): void {
    this._groupingDiv142 = flg;
  }
  public groupingDiv163(flg: string): void {
    this._groupingDiv163 = flg;
  }
  public groupingDiv185(flg: string): void {
    this._groupingDiv185 = flg;
  }
  public groupingDiv240(flg: string): void {
    this._groupingDiv240 = flg;
  }
  public groupingDiv210(flg: string): void {
    this._groupingDiv210 = flg;
  }
  public groupingDiv302(flg: string): void {
    this._groupingDiv302 = flg;
  }
  public groupingDiv515(flg: string): void {
    this._groupingDiv515 = flg;
  }
  public groupingDiv451(flg: string): void {
    this._groupingDiv451 = flg;
  }
  public groupingDiv454(flg: string): void {
    this._groupingDiv454 = flg;
  }

  /**
   * コンポーネント終了時
   *
   * @memberof GroupingCodeComboBox
   */
  public clear(): void {
    this.selectedGroupingCode = null;
    this.selectedGroupingName = null;
    this.selectedIndex = <number>-1;
    this.model = new GroupingCodeComboBoxModel();
    this.model.component = this;
    this.model.blank = this.blank;
  }

}
